import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faPlus, faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAircraftManagement, useFlightManagement } from '../../hooks';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';
import { useAircraftCheck } from '../../hooks/useAircraftCheck';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import ProfileCompletionModal from '../ProfileCompletionModal/ProfileCompletionModal';
import toast from 'react-hot-toast';
import FlightManagementTable from '../FlightManagementTable/FlightManagementTable';

// Helper function to format dates in user's local timezone
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const FlightManagement = () => {
  const navigate = useNavigate();
  const { flights, loading, error, loadFlights, removeFlight, restoreDeletedFlight, permanentlyDeleteFlight, cancelFlightById, pagination } = useFlightManagement();
  const { hasAircraft } = useAircraftManagement();
  const { isModalOpen, closeModal, checkProfileCompletion } = useProfileCompletion();
  const { checkAircraft } = useAircraftCheck();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [visibleColumns, setVisibleColumns] = useState({
    regNo: true,
    flightNo: true,
    depAirport: true,
    etd: true,
    arrAirport: true,
    eta: true,
    belPrice: true,
    blPrice: true,
    seatPrice: true,
    seatTotal: true,
    seatAvailable: true,
    flightTime: true,
    status: true,
    action: true
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedFlightId, setSelectedFlightId] = useState(null);

  // Load flights when pagination, search, status filter, or sort changes
  useEffect(() => {
    const params = {
      page: currentPage,
      limit: recordsPerPage,
      search: searchTerm,
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction
    };
    if (statusFilter) {
      params.status = statusFilter;
    }
    loadFlights(params);
  }, [loadFlights, currentPage, recordsPerPage, searchTerm, statusFilter, sortConfig]);

  // Use flights directly from API (which now include index_id from backend)
  const processedFlights = flights;

  // Use data directly from processed flights (server-side handles filtering and pagination)
  const totalRecords = pagination.totalItems || 0;
  const totalPages = pagination.totalPages || 1;

  console.log('📊 FlightManagement Pagination:', {
    totalRecords,
    totalPages,
    currentPage,
    recordsPerPage,
    flightsLength: flights.length
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    console.log('📄 Page changed to:', pageNum);
    setCurrentPage(pageNum);
  };

  const handleSortChange = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExport = (format) => {
    // Get visible column keys (exclude 'action')
    const visibleCols = Object.keys(visibleColumns).filter(key => visibleColumns[key] && key !== 'action');

    const labels = {
      regNo: 'Reg No.',
      flightNo: 'Flight No.',
      depAirport: 'Dep. Airport',
      etd: 'ETD',
      arrAirport: 'Arr. Airport',
      eta: 'ETA',
      belPrice: 'BEL Price',
      blPrice: 'EL Price',
      seatPrice: 'Seat Price',
      seatTotal: 'Total Seat',
      seatAvailable: 'Available Seat',
      flightTime: 'Flight Time',
      status: 'Status'
    };

    const dataToExport = processedFlights.map((flight) => {
      const row = { '#': flight.seq_id };
      visibleCols.forEach(key => {
        let val = '';
        switch (key) {
          case 'regNo':
            val = flight.registration_number || flight.aircraft?.registration_number || 'N/A';
            break;
          case 'flightNo':
            val = flight.flight_number || 'N/A';
            break;
          case 'depAirport':
            val = flight.dep_airport || flight.departureAirport?.iata_code || 'N/A';
            break;
          case 'etd':
            val = formatDateTime(flight.departure_date_time);
            break;
          case 'arrAirport':
            val = flight.arr_airport || flight.arrivalAirport?.iata_code || 'N/A';
            break;
          case 'eta':
            val = formatDateTime(flight.arrival_date_time);
            break;
          case 'belPrice':
            val = `$${flight.block_empty_price || 0}`;
            break;
          case 'blPrice':
            val = `$${flight.block_price || 0}`;
            break;
          case 'seatPrice':
            val = `$${flight.seat_price || 0}`;
            break;
          case 'seatTotal':
            val = flight.seat_total || 0;
            break;
          case 'seatAvailable':
            val = flight.seat_available || 0;
            break;
          case 'flightTime':
            val = flight.flight_time || 'N/A';
            break;
          case 'status':
            const statusValue = (flight.status || '').toLowerCase();
            const notOperateFlight = Number(flight.not_operate_flight);
            const bookingCount = Number(flight.booking_count);
            if (statusValue === 'published') {
              if (notOperateFlight === 1) {
                val = bookingCount === 0 ? 'DNO' : 'Completed';
              } else {
                val = 'Active';
              }
            } else if (statusValue === 'canceled' || statusValue === 'cancelled') {
              val = 'Cancelled';
            } else if (statusValue === 'draft' || statusValue === 'saved') {
              val = 'Saved';
            } else if (flight.deleted_at) {
              val = 'Deleted';
            } else {
              val = flight.status || 'N/A';
            }
            break;
          default:
            val = '';
        }
        row[labels[key] || key] = val;
      });
      return row;
    });

    if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Flights");
      const fileName = `flight_data_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } else if (format === 'pdf') {
      const doc = new jsPDF('landscape');
      const tableColumn = ["#", ...visibleCols.map(key => labels[key] || key)];
      const tableRows = dataToExport.map(row => Object.values(row));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [255, 235, 0], textColor: [0, 0, 0] } // Yellow background, black text to match theme
      });
      doc.text("Flight Management List", 14, 15);
      const fileName = `flight_data_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    }

    setShowExportDropdown(false);
  };

  const handleDeleteFlight = async (flightId) => {
    setSelectedFlightId(flightId);
    setConfirmAction('delete');
    setShowConfirmModal(true);
  };

  const handleCancelFlight = async (flightId) => {
    setSelectedFlightId(flightId);
    setConfirmAction('cancel');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    setShowConfirmModal(false);

    try {
      if (confirmAction === 'delete') {
        await removeFlight(selectedFlightId);
        toast.success('Flight deleted successfully!');
      } else if (confirmAction === 'cancel') {
        await cancelFlightById(selectedFlightId);
        toast.success('Flight cancelled successfully!');
        loadFlights();
      } else if (confirmAction === 'restore') {
        await restoreDeletedFlight(selectedFlightId);
        toast.success('Flight restored successfully!');
      } else if (confirmAction === 'permanentDelete') {
        await permanentlyDeleteFlight(selectedFlightId);
        toast.success('Flight permanently deleted successfully!');
      }
      if (confirmAction !== 'cancel') {
        loadFlights();
      }
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : error?.message || `Failed to ${confirmAction} flight`;
      toast.error(message);
    } finally {
      setSelectedFlightId(null);
      setConfirmAction(null);
    }
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
    setSelectedFlightId(null);
    setConfirmAction(null);
  };

  const handleRestoreFlight = async (flightId) => {
    setSelectedFlightId(flightId);
    setConfirmAction('restore');
    setShowConfirmModal(true);
  };

  const handlePermanentDeleteFlight = async (flightId) => {
    setSelectedFlightId(flightId);
    setConfirmAction('permanentDelete');
    setShowConfirmModal(true);
  };

  const handleViewFlight = (flightId) => {
    navigate(`/airliner/flight/${flightId}`);
  };

  const handleEditFlight = (flightId) => {
    navigate(`/airliner/edit-flight/${flightId}`);
  };

  return (
    <div className="p-0 lg:ml-6 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex flex-col">
      <div className="flex-1">
        <div className="py-5 px-[15px] md:px-[30px] mb-0 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0 bg-transparent">
          <h1 className="m-0 text-lg text-[#333] font-bold mb-0 hidden md:block">Flight Management</h1>
          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center w-full md:w-auto">
            <div className="relative w-full sm:w-auto">
              <button
                className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
              >
                <FontAwesomeIcon icon={faFileExport} /> Export
              </button>
              {showExportDropdown && (
                <div className="absolute top-full left-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded p-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[155px] mt-[5px] overflow-hidden">
                  <div onClick={() => handleExport('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                    <FontAwesomeIcon icon={faFileExcel} /> Excel
                  </div>
                  <div onClick={() => handleExport('pdf')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                    <FontAwesomeIcon icon={faFilePdf} /> PDF
                  </div>
                </div>
              )}
            </div>
            <div className="relative w-full sm:w-auto">
              <button
                className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              >
                Hide/Show Column ▼
              </button>
              {showColumnDropdown && (
                <div className="absolute top-full right-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[250px] mt-[5px] max-h-[400px] overflow-y-auto">
                  {Object.keys(visibleColumns).map(key => (
                    <label
                      key={key}
                      onClick={() => toggleColumnVisibility(key)}
                      className="flex items-center py-2 px-5 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                    >
                      <span className="inline-flex items-center justify-center">
                        <span className="inline-flex items-center justify-center w-4 h-4 min-w-[16px] border border-[#666] rounded-sm bg-white">
                          {visibleColumns[key] && <span className="text-[#2ecc71] text-[14px] font-bold leading-none">✓</span>}
                        </span>
                      </span>
                      <span className="flex-1 whitespace-nowrap">
                        {key === 'regNo' && 'Reg No.'}
                        {key === 'flightNo' && 'Flight No.'}
                        {key === 'depAirport' && 'Dep. Airport'}
                        {key === 'etd' && 'ETD'}
                        {key === 'arrAirport' && 'Arr. Airport'}
                        {key === 'eta' && 'ETA'}
                        {key === 'belPrice' && 'BEL Price'}
                        {key === 'blPrice' && 'BL Price'}
                        {key === 'seatPrice' && 'Seat Price'}
                        {key === 'seatTotal' && 'Total Seat'}
                        {key === 'seatAvailable' && 'Available Seat'}
                        {key === 'flightTime' && 'Flight Time'}
                        {key === 'status' && 'Status'}
                        {key === 'action' && 'Action'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]" onClick={() => {
              // First check if user has aircraft
              if (!checkAircraft()) {
                navigate('airliner/add-aircraft')
              }
              // Then check profile completion
              if (checkProfileCompletion()) {
                navigate('/airliner/add-flight');
              }
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '3px' }} /> Add Flight
            </button>
          </div>
        </div>
        <div className="m-[1rem]">
          <FlightManagementTable
            flights={processedFlights}
            loading={loading}
            error={error}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            currentPage={currentPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onPageChange={handlePageChange}
            visibleColumns={visibleColumns}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onViewFlight={handleViewFlight}
            onEditFlight={handleEditFlight}
            onDeleteFlight={handleDeleteFlight}
            onCancelFlight={handleCancelFlight}
            onRestoreFlight={handleRestoreFlight}
            onPermanentDeleteFlight={handlePermanentDeleteFlight}
          />
        </div>
      </div>

      {/* Copyright Section */}
      <div className="pt-0 p-[15px] mr-auto ml-auto px-[15px] bg-jet-gray-100 w-full">
        <div className="bg-white p-[10px] md:p-[15px] text-black border-t-0 text-center shadow-none pb-[10px] rounded-[5px] text-[12px] md:text-[14px]">
          Copyright © 2026 Jetscanner360. All rights reserved.
        </div>
      </div>

      {/* Profile Incomplete Modal */}
      <ProfileCompletionModal
        isOpen={isModalOpen}
        onClose={closeModal}
      />

      {/* Confirm Action Modal */}
      <UniversalConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmAction}
        onClose={handleCancelModal}
        type={confirmAction}
        title={confirmAction === 'delete' ? 'Are you sure?' : undefined}
        message={confirmAction === 'delete' ? 'There is a reservation for this flight. Click "Delete" to proceed, "Cancel" to exit.' : undefined}
        confirmText={confirmAction === 'delete' ? 'DELETE' : undefined}
        cancelText={confirmAction === 'delete' ? 'CANCEL' : undefined}
        theme="modern"
      />
    </div>
  );
};

export default FlightManagement;
